import { Placement } from '@popperjs/core';
import { ReactComponent as ErrorIcon } from 'assets/alerts/error.svg';
import { ReactComponent as InfoIcon } from 'assets/alerts/info.svg';
import { ReactComponent as WarningIcon } from 'assets/alerts/warningOutline.svg';
import { ReactComponent as VersionIcon } from 'assets/icon/copy.svg';
import FieldMessage from 'components/FieldMessage';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { usePopper } from 'react-popper';
import styled from 'styled-components';

const Wrapper = styled.span`
  position: relative;
  display: flex;
  padding-left: 14px;
`;

const PopperElement = styled.div`
  z-index: 12;
`;

const FieldMessagePopper = styled(FieldMessage)<{
  $isLongString?: boolean;
}>`
  white-space: ${({ $isLongString }) => ($isLongString ? 'initial' : 'nowrap')};
  margin: 0 8px;
  display: block !important;
  width: ${({ $isLongString }) => ($isLongString ? '400px' : 'initial')};

  @media screen and (max-width: 1200px) {
    transform: translateY(-1px);
  }
`;

const Button = styled.span`
  border: 0;
  background: transparent;
  display: inline-block;
  position: relative;
  z-index: 11;
  cursor: pointer;
`;

export enum MessageType {
  INFO,
  WARNING,
  ERROR,
  VERSION,
}

type Props = {
  id?: string;
  msg: string | ReactElement;
  type?: MessageType;
  className?: string;
  show?: boolean;
  style?: React.CSSProperties;
  direction?: Placement;
  isLongString?: boolean;
};

const Icon = ({ id, type }: { id: string; type: MessageType }) => {
  if (type === MessageType.INFO) {
    return (
      <InfoIcon
        id={id}
        style={{
          display: 'block',
        }}
      />
    );
  } else if (type === MessageType.ERROR) {
    return (
      <ErrorIcon
        id={id}
        style={{
          display: 'block',
        }}
      />
    );
  } else if (type === MessageType.VERSION) {
    return (
      <VersionIcon
        id={id}
        style={{
          display: 'block',
        }}
      />
    );
  } else {
    return (
      <WarningIcon
        id={id}
        style={{
          display: 'block',
        }}
      />
    );
  }
};

const Popper: React.VFC<Props> = ({
  id,
  msg,
  type = MessageType.WARNING,
  className,
  show,
  style,
  direction,
  isLongString = false,
}) => {
  const [isShow, setIsShown] = useState<boolean>(false);
  const referenceElement = useRef<HTMLButtonElement>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | undefined | null>(null);
  const { styles, attributes, update } = usePopper(referenceElement.current, popperElement, {
    placement: direction ? direction : 'right',
    modifiers: [{ name: 'offset', options: { offset: [0, 1] } }],
  });

  useEffect(() => {
    if (isShow && update) {
      update();
    }
  }, [isShow, update]);

  return (
    <Wrapper className={className} style={style}>
      <Button
        data-popper={type === 0 ? 'info' : type === 1 ? 'warning' : 'error'}
        id={`${id}-popper`}
        ref={referenceElement}
        {...(!isMobile
          ? {
              onMouseEnter: () => setIsShown(true),
              onMouseLeave: () => setIsShown(false),
            }
          : {
              onClick: () => {
                setIsShown((isShow) => !isShow);
              },
              onMouseLeave: () => setIsShown(false),
            })}
      >
        <Icon id={`${id}-popper-svg`} type={type} />
      </Button>

      {isShow || show ? (
        <PopperElement
          id={`${id}-fieldmessage`}
          ref={setPopperElement}
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <FieldMessagePopper $isLongString={isLongString} isShowIcon={false} type={type}>
            {msg}
          </FieldMessagePopper>
        </PopperElement>
      ) : null}
    </Wrapper>
  );
};

export default Popper;
